import { State } from "redux/store";
import { Helmet } from "react-helmet";
import { connect, useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router";
import { App } from "antd";

import {
  getExportCount,
  getExportDataTimeoutID,
  getExportTags,
  getFilteredSimilarityClusters,
  getNavigationCluster,
  getShowCart,
  getStats,
  getTempExportData,
  getAssignedTags,
  getExportRequestData,
  getShowExportModal,
  getExportDataArray,
  getDatasetTags,
} from "redux/SingleDataset/selectors";
import {
  setShowCart,
  fetchAssignedTags,
  setAssignedTags,
  submitRemoveTagsRequest,
  setShowExportModal,
  fetchDatasetTags,
  clearExportData,
  setLastSelectedIndex,
} from "redux/SingleDataset/actions";

import { useWindowResize } from "helpers/utility/utilities";
import { ExportModalSource, FilterOption } from "types";
import SingleItem from "./SingleItem";
import TagsDropDown from "./TagsDropDown";
import TagIcon from "assets/icons/tagIcon";
import NotesIcon from "assets/icons/NotesIcon";
import ExportIcon from "assets/icons/ExportIcon";
import AssignTagsToast from "./AssignTagsToast";
import ExportModal from "views/modals/ExportModal";

import fancyRightArrow from "assets/img/fancy-right-arrow.svg";

import styles from "./style.module.scss";
import { toast } from "react-toastify";
import { ERROR_RED, PARROT_GREEN } from "helpers/constants/colors";
import { formatNumberWithCommas } from "helpers/utility/formatters";
import classNames from "classnames";
import tagIcon from "assets/img/tag-icon-black.svg";
import TodoIcon from "assets/img/colored-to-do-list.svg";
import SettingsIcon from "assets/img/colored-setting.svg";
import FolderIcon from "assets/img/colored-file-storage.svg";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import { useTagAssigment } from "./TagsDropDown/useTagAssigment";
import VLTooltip from "views/uikit/VLTooltip";

const Cart = ({
  exportCount,
  showCart,
  originalTags,
  exportTags,
  datasetID,
  exportDataArray,
  assignedTags,
  showExportModal,
}: any) => {
  const { modal } = App.useApp();
  const { clusterId } = useParams();
  const toastIdRef = useRef<any>(null);
  const exportButtonRef = useRef<HTMLDivElement>(null);
  const cartContainerRef = useRef<HTMLDivElement>(null);
  const tagDropdownBtnRef = useRef<HTMLDivElement>(null);

  const [showTagsDropdown, setShowTagsDropdown] = useState(false);
  const [count, setCount] = useState(0);
  const [hasDispatchedDatasetTags, setHasDispatchedDatasetTags] =
    useState(false);

  const queryParamsFilters = useAllQueryParamsFilters();
  const exportRequestData = useSelector((state: State) =>
    getExportRequestData(state, queryParamsFilters),
  );

  const dispatch = useDispatch();
  const { height } = useWindowResize();

  const cartBodyClassNames = classNames(styles.cartBody, {
    [styles.hideScroll]: count > 0,
  });

  const cartContainerClassNames = classNames(styles.cartContainer, {
    [styles.hideCartContainer]: !showCart,
  });
  const cartBackgroundClassNames = classNames(styles.cartBackground, {
    [styles.hideCartBackground]: !showCart,
  });

  const toastStyle = {
    borderRadius: "10px",
    marginBottom: "10px",
  };
  const { handleTagSubmit } = useTagAssigment();

  const toggleShowTagsDropdown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setShowTagsDropdown(!showTagsDropdown);
  };

  const handleClearAll = () => {
    modal.confirm({
      title: "Clear All",
      content: "This will clear all of the items selected in this Dataset",
      okText: "Clear All",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => {
        dispatch(clearExportData(datasetID) as any);
        dispatch(setShowCart(false));
        dispatch(setLastSelectedIndex(null));
      },
    });
  };

  const handleOutsideClick = (event: any) => {
    if (
      cartContainerRef.current &&
      !cartContainerRef.current.contains(event.target)
    ) {
      dispatch(setShowCart(!showCart));
      setShowTagsDropdown(false);
      dispatch(setShowExportModal(null));
    }
  };

  const handleExportClick = () => {
    dispatch(
      setShowExportModal(!showExportModal ? ExportModalSource.CART : null),
    );
  };

  const successNotification = (type: ToastActionType) => {
    const dynamicDescription =
      type === "assign"
        ? `Tag successfully assigned to ${exportCount}  ${
            exportCount > 1 ? "items" : "item"
          } `
        : `Tag successfully de-assigned from ${exportCount} ${
            exportCount > 1 ? "items" : "item"
          }  `;
    toastIdRef.current = toast(
      <AssignTagsToast
        heading={`Tag assignment`}
        description={dynamicDescription}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,

      {
        style: { ...toastStyle, border: `2px solid ${PARROT_GREEN}` },
        closeButton: false,
      },
    );
  };

  const errorNotification = (type: ToastActionType) => {
    const dynamicDescription =
      type === "assign"
        ? "Error occoured while tagging. Please try again"
        : "Error occoured while removing tags. Please try again";
    toastIdRef.current = toast(
      <AssignTagsToast
        heading={`Error!`}
        description={dynamicDescription}
        onClose={() => toast.dismiss(toastIdRef.current)}
      />,
      {
        closeOnClick: false,
        closeButton: false,
        style: { ...toastStyle, border: `2px solid ${ERROR_RED}` },
      },
    );
  };

  const handleTagSubmission = async (tagIDs: string[]) => {
    const response = await handleTagSubmit(tagIDs);
    if (response.success) {
      successNotification("assign");
    } else {
      dispatch(
        fetchAssignedTags(datasetID, exportRequestData, assignedTags) as any,
      );
      toast.dismiss(toastIdRef.current);
      dispatch(setShowCart(false));
      errorNotification("assign");
    }
  };

  const handleTagRemoval = async (tagID: string) => {
    try {
      let requestBody: any = { [tagID]: exportRequestData };
      await dispatch(
        submitRemoveTagsRequest(
          datasetID,
          requestBody,
          queryParamsFilters,
          clusterId,
        ) as any,
      );
      successNotification("remove");
    } catch (error) {
      toast.dismiss(toastIdRef.current);
      errorNotification("remove");
    }
  };

  const renderManageTagsButton = () => {
    const manageTagBtnClassnames = classNames(styles.actionButton, {
      [styles.selectedActionButton]: showTagsDropdown,
    });

    return (
      <VLTooltip title={!showTagsDropdown ? "Manage Tags" : ""}>
        <div
          ref={tagDropdownBtnRef}
          onClick={toggleShowTagsDropdown}
          className={manageTagBtnClassnames}
        >
          <TagIcon />

          {showTagsDropdown && (
            <div className={styles.tagsDropdownPositioner}>
              <TagsDropDown
                assignedTags={assignedTags.filter(
                  (t: FilterOption) => t.selected,
                )}
                allTags={originalTags}
                toggleShowTags={toggleShowTagsDropdown}
                onTagSubmission={handleTagSubmission}
                onTagRemoval={handleTagRemoval}
                datasetID={datasetID}
                btnRef={tagDropdownBtnRef}
              />
            </div>
          )}
        </div>
      </VLTooltip>
    );
  };

  const renderExportOption = () => {
    const exportBtnClassnames = classNames(styles.actionButton, {
      [styles.selectedActionButton]: showExportModal,
    });

    return (
      <>
        <VLTooltip title={!showExportModal ? "Export" : ""}>
          <div
            ref={exportButtonRef}
            onClick={handleExportClick}
            className={exportBtnClassnames}
          >
            <ExportIcon />
            {showCart && (
              <div className={styles.exportModalPositioner}>
                <ExportModal
                  openedFrom={ExportModalSource.CART}
                  showModal={showExportModal === ExportModalSource.CART}
                  callerButtonRef={exportButtonRef}
                />
              </div>
            )}
          </div>
        </VLTooltip>
      </>
    );
  };

  useEffect(() => {
    if (exportDataArray.length > 0) {
      dispatch(
        fetchAssignedTags(datasetID, exportRequestData, assignedTags) as any,
      );
    } else {
      dispatch(setAssignedTags([]));
    }
    //eslint-disable-next-line
  }, [exportDataArray, datasetID]);

  //calculates the count of the placeholders
  useEffect(() => {
    const cartContainer = cartContainerRef.current;
    const selectedCards = exportDataArray.length;

    let totalHeight;
    if (cartContainer) totalHeight = cartContainer.offsetHeight - 50;
    if (totalHeight) {
      // 134 is single element's height,
      let requiredCount = Math.round(totalHeight / 134);

      if (selectedCards < requiredCount) {
        setCount(requiredCount - selectedCards);
      } else if (selectedCards >= requiredCount) {
        setCount(0);
      }
    }
  }, [showCart, exportDataArray, height]);

  useEffect(() => {
    if (showCart) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showCart]);

  useEffect(() => {
    if (!showCart || !datasetID || hasDispatchedDatasetTags) return;

    dispatch(fetchDatasetTags(datasetID) as any);
    setHasDispatchedDatasetTags(true);
  }, [datasetID, hasDispatchedDatasetTags, dispatch, showCart]);

  return (
    <div className={cartBackgroundClassNames}>
      {/* preload icons */}
      <Helmet>
        <link rel="preload" href={tagIcon} as="image" type="image/svg+xml" />
        <link rel="preload" href={TodoIcon} as="image" type="image/svg+xml" />
        <link
          rel="preload"
          href={SettingsIcon}
          as="image"
          type="image/svg+xml"
        />
        <link rel="preload" href={FolderIcon} as="image" type="image/svg+xml" />
      </Helmet>
      <div className={styles.cartWrapper}>
        <div ref={cartContainerRef} className={cartContainerClassNames}>
          <div className={styles.cartHeader}>
            <div className={styles.savedItemsContainer}>
              <div className={styles.savedItemsIcon}>
                <NotesIcon />
              </div>
              <span className={styles.savedItemsText}>{`Selected Items (${
                exportCount ? formatNumberWithCommas(exportCount) : 0
              })`}</span>
            </div>

            <div className={styles.actionButtons}>
              {(exportCount > 0 || exportTags.length > 0) && (
                <>
                  <div
                    className={styles.removeAllButton}
                    onClick={handleClearAll}
                  >
                    Clear All
                  </div>

                  <div className={styles.separator}></div>
                </>
              )}

              <div className={styles.buttonGroup}>
                {renderManageTagsButton()}
                {renderExportOption()}
              </div>
            </div>
          </div>

          <div className={cartBodyClassNames}>
            <div className={styles.itemsList}>
              {exportDataArray.map((singleItem: any, index: number) => (
                <SingleItem
                  key={index}
                  singleItem={singleItem}
                  datasetID={datasetID}
                />
              ))}
              {count > 0 &&
                [...Array(count)].map((_, index) => (
                  <div key={index} className={styles.placeholderBox}>
                    {exportDataArray.length <= 0 && index === 0 && (
                      <div className={styles.noSelectionBox}>
                        <img
                          src={fancyRightArrow}
                          className={styles.noSelectionIcon}
                          alt="right-arrow-icon"
                        />

                        <div className={styles.noSelectionText}>
                          No selected items yet.
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    exportCount: getExportCount(state),
    showCart: getShowCart(state),
    originalTags: getDatasetTags(state),
    exportTags: getExportTags(state),
    datasetID: getStats(state)?.dataset?.id,
    clusters: getFilteredSimilarityClusters(state),
    navigationCluster: getNavigationCluster(state),
    tempExportData: getTempExportData(state),
    timeoutID: getExportDataTimeoutID(state),
    exportDataArray: getExportDataArray(state),
    assignedTags: getAssignedTags(state),
    showExportModal: getShowExportModal(state),
  };
};
export default connect(mapStateToProps)(Cart);

type ToastActionType = "assign" | "remove";
